import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Clock,
  Compass,
  MapPin,
  PackageOpen,
  Phone,
  Search,
  Store,
  X,
} from 'lucide-react'
import { publicClient } from '../../api/publicClient'
import { AddressModal } from './AddressModal'
import IconAsalinkPrimary from '../../assets/img/Icon ASAlink Primary.png'
import './Ecatalog.css'

const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
})

function valueOf(value, fallback = '') {
  return value === null || value === undefined || value === '' ? fallback : value
}

function apiData(response) {
  return response?.data ?? response
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="ecatalog-state">
      <PackageOpen size={28} strokeWidth={1.6} />
      <h2>Belum bisa memuat katalog</h2>
      <p>{message || 'Periksa koneksi internet lalu coba lagi.'}</p>
      <button type="button" onClick={onRetry}>Coba lagi</button>
    </div>
  )
}

function LoadingState({ label = 'Memuat...' }) {
  return (
    <div className="ecatalog-state" aria-live="polite">
      <Compass size={28} strokeWidth={1.6} />
      <p>{label}</p>
    </div>
  )
}

function Brand({ back = false }) {
  return (
    <Link className="ecatalog-brand" to="/e-katalog">
      <img src={IconAsalinkPrimary} alt="ASAlink" className="ecatalog-brand-logo" />
      <span className="ecatalog-brand-text">{back ? 'Kembali' : 'ASAlink E-Katalog'}</span>
    </Link>
  )
}

function ImageOrInitial({ src, name, className = 'ecatalog-avatar' }) {
  const [failed, setFailed] = useState(false)
  return (
    <span className={className}>
      {src && !failed ? (
        <img src={src} alt="" onError={() => setFailed(true)} />
      ) : (
        name?.trim()?.charAt(0)?.toUpperCase() || <Store size={22} />
      )}
    </span>
  )
}

function DiscoveryOutlet({ outlet }) {
  const name = valueOf(outlet.public_name, 'Outlet')
  const distance = outlet.distance_km !== undefined && outlet.distance_km !== null
    ? `${Number(outlet.distance_km).toFixed(1)} Km`
    : null

  return (
    <Link className="ecatalog-outlet" to={`/e-katalog/${encodeURIComponent(outlet.slug)}`}>
      <ImageOrInitial src={outlet.logo_url} name={name} />
      <span className="ecatalog-outlet-content">
        <h2 className="ecatalog-outlet-name">{name}</h2>
        <span className="ecatalog-outlet-meta">
          {distance && (
            <span className="ecatalog-outlet-meta-item">
              <MapPin size={13} className="ecatalog-pin-red" />
              {distance}
            </span>
          )}
          {outlet.is_open ? (
            <span className="ecatalog-outlet-meta-item ecatalog-text-success">
              <Clock size={13} /> Buka sekarang
            </span>
          ) : outlet.open_label ? (
            <span className="ecatalog-outlet-meta-item">
              <Clock size={13} /> {outlet.open_label}
            </span>
          ) : null}
          {outlet.store_city && <span>{outlet.store_city}</span>}
        </span>
        <span className="ecatalog-chip-row">
          {outlet.fulfillment_modes?.map((mode) => (
            <span className="ecatalog-chip" key={mode}>{mode}</span>
          ))}
        </span>
      </span>
      <ChevronRight size={18} className="ecatalog-outlet-arrow" />
    </Link>
  )
}

export function EcatalogDiscovery() {
  const [query, setQuery] = useState('')
  const [outlets, setOutlets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddressModal, setShowAddressModal] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(() => {
    try {
      const saved = localStorage.getItem('ecatalog_saved_location')
      return saved ? JSON.parse(saved) : null
    } catch {
      return null
    }
  })

  const loadOutlets = async (coords = null, searchKeyword = '') => {
    setLoading(true)
    setError('')
    try {
      const params = {}
      if (searchKeyword.trim()) params.q = searchKeyword.trim()
      if (coords?.lat && coords?.lng) {
        params.lat = coords.lat
        params.lng = coords.lng
      }
      const result = await publicClient.discovery(params)
      setOutlets(apiData(result) || [])
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedLocation?.lat && selectedLocation?.lng) {
      loadOutlets({ lat: selectedLocation.lat, lng: selectedLocation.lng }, query)
    } else {
      loadOutlets(null, query)
    }
  }, [])

  const submitSearch = (event) => {
    event.preventDefault()
    loadOutlets(selectedLocation, query)
  }

  const handleSelectLocation = (loc) => {
    setSelectedLocation(loc)
    try {
      localStorage.setItem('ecatalog_saved_location', JSON.stringify(loc))
    } catch (e) {
      console.warn('Cannot save location to localStorage', e)
    }
    loadOutlets({ lat: loc.lat, lng: loc.lng }, query)
  }

  const inRangeOutlets = useMemo(() => {
    if (!selectedLocation) return outlets
    return outlets.filter((o) => o.is_in_range !== false)
  }, [outlets, selectedLocation])

  const outOfRangeOutlets = useMemo(() => {
    if (!selectedLocation) return []
    return outlets.filter((o) => o.is_in_range === false)
  }, [outlets, selectedLocation])

  const locationDisplayText = selectedLocation
    ? selectedLocation.fullAddress || selectedLocation.title
    : 'Pilih lokasi pengiriman Anda'

  return (
    <main className="ecatalog-page">
      <div className="ecatalog-container">
        {/* Top Header: Brand + Single Unified Location Selector */}
        <header className="ecatalog-topbar">
          <Brand />
          
          <button
            type="button"
            className="ecatalog-topbar-location-btn"
            onClick={() => setShowAddressModal(true)}
            aria-label="Pilih lokasi pengiriman"
          >
            <span className="ecatalog-topbar-location-sub">Lokasi saat ini</span>
            <span className="ecatalog-topbar-location-main">
              <MapPin size={15} className="ecatalog-pin-red" />
              <span className="ecatalog-topbar-location-text">
                {locationDisplayText}
              </span>
              <ChevronDown size={14} className="ecatalog-topbar-location-chevron" />
            </span>
          </button>
        </header>

        {/* Intro & Search */}
        <section className="ecatalog-intro">
          <p className="ecatalog-eyebrow">Pesan dari outlet terdekat</p>
          <h1 className="ecatalog-title">Temukan menu favoritmu.</h1>
          <p className="ecatalog-subtitle">
            Cari outlet kuliner di sekitar Anda, lihat menu yang tersedia, lalu pesan dengan mudah.
          </p>

          <form className="ecatalog-search" onSubmit={submitSearch}>
            <Search size={19} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Cari nama outlet atau kota"
              aria-label="Cari outlet"
            />
            <button type="submit" disabled={loading}>Cari</button>
          </form>
        </section>

        {/* Section List: In-Range vs Out-Of-Range */}
        <div className="ecatalog-toolbar">
          <h2 className="ecatalog-section-title">
            {selectedLocation ? 'Outlet di Sekitarmu' : 'Semua Outlet'}
          </h2>
          <span className="ecatalog-outlet-count-badge">
            {inRangeOutlets.length} outlet tersedia
          </span>
        </div>

        {loading ? (
          <LoadingState label="Mencari outlet..." />
        ) : error ? (
          <ErrorState message={error} onRetry={() => loadOutlets(selectedLocation, query)} />
        ) : outlets.length === 0 ? (
          <div className="ecatalog-state">
            <Store size={28} strokeWidth={1.6} />
            <h2>Outlet belum ditemukan</h2>
            <p>Coba kata kunci lain atau pilih lokasi pengiriman Anda.</p>
          </div>
        ) : (
          <>
            {inRangeOutlets.length > 0 && (
              <div className="ecatalog-outlet-list">
                {inRangeOutlets.map((outlet) => (
                  <DiscoveryOutlet key={outlet.slug} outlet={outlet} />
                ))}
              </div>
            )}

            {outOfRangeOutlets.length > 0 && (
              <div className="ecatalog-outofrange-section">
                <div className="ecatalog-toolbar">
                  <h2 className="ecatalog-section-title ecatalog-section-title-muted">
                    Outlet Tidak Terjangkau Pengiriman
                  </h2>
                </div>
                <div className="ecatalog-outlet-list">
                  {outOfRangeOutlets.map((outlet) => (
                    <DiscoveryOutlet key={outlet.slug} outlet={outlet} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Address Picker Modal with Leaflet Map & Search */}
      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        initialCoords={selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : null}
        initialAddress={selectedLocation?.fullAddress}
        onSelectLocation={handleSelectLocation}
      />
    </main>
  )
}

function ProductImage({ product }) {
  const primaryImage = product.images?.find((image) => image.is_primary) || product.images?.[0]
  return <div className="ecatalog-product-image">{(primaryImage?.image_url || product.image_url) ? <img src={primaryImage?.image_url || product.image_url} alt="" /> : <PackageOpen size={25} strokeWidth={1.5} />}</div>
}

function ProductCard({ product, onSelect }) {
  const unavailable = Number(product.stock) <= 0
  return (
    <button className={`ecatalog-product ${unavailable ? 'ecatalog-unavailable' : ''}`} type="button" onClick={() => onSelect(product)}>
      <ProductImage product={product} />
      <span className="ecatalog-product-info">
        <span className="ecatalog-product-badges">{product.is_featured && <span className="ecatalog-badge">Rekomendasi</span>}{product.is_best_seller && <span className="ecatalog-badge">Terlaris</span>}</span>
        <h2>{product.name}</h2>
        {product.description && <p>{product.description}</p>}
        <strong className="ecatalog-price">{rupiah.format(product.sell_price || 0)}</strong>
        {unavailable && <span className="ecatalog-outlet-meta">Stok habis</span>}
      </span>
    </button>
  )
}

function ProductSheet({ product, onClose }) {
  if (!product) return null
  return (
    <div className="ecatalog-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="ecatalog-modal" role="dialog" aria-modal="true" aria-labelledby="product-sheet-title">
        <div className="ecatalog-modal-inner">
          <div className="ecatalog-modal-header"><div><h2 id="product-sheet-title">{product.name}</h2><strong className="ecatalog-price">{rupiah.format(product.sell_price || 0)}</strong></div><button className="ecatalog-icon-button" type="button" onClick={onClose} aria-label="Tutup detail"><X size={19} /></button></div>
          {product.description && <p className="ecatalog-modal-description">{product.description}</p>}
          {product.variants?.length > 0 && <div className="ecatalog-variant-list">{product.variants.map((variant) => <div className="ecatalog-variant" key={variant.id}><strong>{variant.name}</strong><span>{rupiah.format(variant.sell_price || 0)}</span></div>)}</div>}
          {product.variants?.length === 0 && <p className="ecatalog-modal-description">Pilihan produk akan tersedia saat pemesanan dibuka.</p>}
        </div>
      </section>
    </div>
  )
}

export function EcatalogCatalog() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [catalog, setCatalog] = useState(null)
  const [activeCategory, setActiveCategory] = useState('all')
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadCatalog = async () => {
    setLoading(true)
    setError('')
    try {
      setCatalog(apiData(await publicClient.catalog(slug)))
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadCatalog() }, [slug])

  const products = useMemo(() => {
    const allProducts = catalog?.products || []
    return activeCategory === 'all' ? allProducts : allProducts.filter((product) => product.category_id === activeCategory)
  }, [catalog, activeCategory])

  if (loading) return <main className="ecatalog-page"><div className="ecatalog-container"><header className="ecatalog-topbar"><Brand back /></header><LoadingState label="Memuat menu..." /></div></main>
  if (error || !catalog?.profile) return <main className="ecatalog-page"><div className="ecatalog-container"><header className="ecatalog-topbar"><Brand back /></header><ErrorState message={error || 'Katalog tidak ditemukan.'} onRetry={loadCatalog} /></div></main>

  const profile = catalog.profile
  return (
    <main className="ecatalog-page">
      <div className="ecatalog-container">
        <header className="ecatalog-topbar"><button className="ecatalog-icon-button" type="button" onClick={() => navigate(-1)} aria-label="Kembali"><ArrowLeft size={19} /></button><Brand /></header>
        <section className="ecatalog-store-header">
          <div className="ecatalog-banner">{profile.banner_url && <img src={profile.banner_url} alt="" />}</div>
          <div className="ecatalog-store-summary"><ImageOrInitial src={profile.logo_url} name={profile.public_name} /><div className="ecatalog-store-copy"><h1>{profile.public_name}</h1>{profile.description && <p>{profile.description}</p>}</div></div>
          <div className="ecatalog-store-details">{profile.store_city && <span><MapPin size={15} />{profile.store_city}</span>}{profile.store_phone && <span><Phone size={15} />{profile.store_phone}</span>}{profile.fulfillment_modes?.map((mode) => <span key={mode}><PackageOpen size={15} />{mode}</span>)}</div>
        </section>
        <nav className="ecatalog-category-bar" aria-label="Kategori menu"><div className="ecatalog-category-list"><button className={`ecatalog-category ${activeCategory === 'all' ? 'ecatalog-category-active' : ''}`} type="button" onClick={() => setActiveCategory('all')}>Semua</button>{(catalog.categories || []).map((category) => <button className={`ecatalog-category ${activeCategory === category.id ? 'ecatalog-category-active' : ''}`} type="button" key={category.id} onClick={() => setActiveCategory(category.id)}>{category.name}</button>)}</div></nav>
        {products.length === 0 ? <div className="ecatalog-state"><PackageOpen size={28} strokeWidth={1.6} /><h2>Menu belum tersedia</h2><p>Belum ada produk yang dapat ditampilkan di kategori ini.</p></div> : <div className="ecatalog-product-grid">{products.map((product) => <ProductCard key={product.id} product={product} onSelect={setSelectedProduct} />)}</div>}
      </div>
      <ProductSheet product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </main>
  )
}
